import { useCallback, useState, type MouseEvent, type PointerEvent } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { STAGE_INFO } from "@/lib/event-board";
import type { EventStage } from "@/lib/event-pipeline";

/** The small "New" badge beside Post-event. */
export function NewBadge() {
  return (
    <span className="ml-1 inline-block rounded bg-accent px-[5px] py-px align-[1px] text-[10px] font-bold uppercase tracking-[.08em] text-card">
      New
    </span>
  );
}

const KICKER = "block text-[10.5px] font-bold uppercase tracking-[.08em] opacity-65";

/**
 * The ⓘ beside a column heading (#1557): when an event lands in the stage and
 * when it moves on. A mouse hover opens it; a click or tap pins it open, and a
 * second click, Escape or a click outside closes it.
 */
export function StageInfo({ stage }: { stage: EventStage }) {
  const info = STAGE_INFO[stage];
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);

  const onPointerEnter = useCallback(
    (e: PointerEvent) => {
      if (e.pointerType === "mouse" && !pinned) setOpen(true);
    },
    [pinned],
  );
  const onPointerLeave = useCallback(
    (e: PointerEvent) => {
      if (e.pointerType === "mouse" && !pinned) setOpen(false);
    },
    [pinned],
  );
  const onClick = useCallback(
    (e: MouseEvent) => {
      // Radix would toggle on its own; a click pins instead.
      e.preventDefault();
      if (pinned) {
        setPinned(false);
        setOpen(false);
      } else {
        setPinned(true);
        setOpen(true);
      }
    },
    [pinned],
  );
  const onOpenChange = useCallback((next: boolean) => {
    if (next) return;
    setOpen(false);
    setPinned(false);
  }, []);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`When an event is in ${info.label}`}
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
          onClick={onClick}
          className="grid h-[19px] w-[19px] flex-none place-items-center rounded-full border bg-card p-0 font-serif text-[11.5px] font-bold italic text-muted-foreground hover:border-accent hover:text-accent aria-expanded:border-accent aria-expanded:text-accent"
        >
          i
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="grid w-[min(300px,calc(100vw-32px))] gap-[7px] rounded-[10px] border-0 bg-foreground px-3.5 py-3 text-[13.5px] leading-[1.45] text-background shadow-[0_10px_30px_hsl(30_20%_10%/.18)]"
      >
        <h5 className="m-0 font-display text-sm font-bold">
          {info.label}
          {info.isNew && <NewBadge />}
        </h5>
        <div>
          <span className={KICKER}>An event is here when</span>
          {info.in}
        </div>
        <div>
          <span className={KICKER}>It moves on when</span>
          {info.out}
        </div>
        {info.chg && <div className="rounded-md bg-accent px-2 py-1.5 text-[12.5px] text-card">{info.chg}</div>}
      </PopoverContent>
    </Popover>
  );
}
